import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Trash2, Upload, ImageOff } from "lucide-react";
import api, { API, authedFetch, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml"];
const MAX_BYTES = 5 * 1024 * 1024;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * A thumbnail for a private image.
 *
 * The library is not public, so an `<img src>` pointing at the API would get a
 * 401: the tag sends no Authorization header. Each thumbnail is fetched with
 * the authenticated client and rendered from an object URL, which is revoked
 * when the tile goes away so a long session does not leak blobs.
 */
function Thumbnail({ asset }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    authedFetch(`${API}/assets/${asset.id}`)
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => !revoked && setFailed(true));

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id]);

  if (failed) {
    return (
      <div className="h-full w-full flex items-center justify-center text-zinc-600">
        <ImageOff className="w-5 h-5" />
      </div>
    );
  }
  if (!url) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
      </div>
    );
  }
  return <img src={url} alt={asset.name} className="h-full w-full object-cover" />;
}

export default function LibraryDialog({ open, onOpenChange, onPick }) {
  const [assets, setAssets] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/assets")
      .then(({ data }) => setAssets(data))
      .catch((err) => {
        setAssets([]);
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      });
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const upload = async (files) => {
    const images = [...files].filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (images.length === 0) {
      toast.error("Only PNG, JPEG, WebP, GIF and SVG images can be uploaded");
      return;
    }

    setBusy(true);
    let saved = 0;
    for (const file of images) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name || "That image"} is larger than 5 MB`);
        continue;
      }
      try {
        const data = await readAsDataUrl(file);
        await api.post("/assets", { name: file.name, data });
        saved += 1;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      }
    }
    setBusy(false);
    if (saved > 0) {
      toast.success(`${saved} image${saved > 1 ? "s" : ""} added`);
      load();
    }
  };

  const remove = async (asset, event) => {
    event.stopPropagation();
    try {
      await api.delete(`/assets/${asset.id}`);
      setAssets((current) => current.filter((a) => a.id !== asset.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Attaching sends the bytes, so the picked image is fetched once here rather
  // than the composer being taught to resolve library ids.
  const pick = async (asset) => {
    try {
      const res = await authedFetch(`${API}/assets/${asset.id}`);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const data = await readAsDataUrl(blob);
      onPick({ id: asset.id, name: asset.name, data });
      onOpenChange(false);
    } catch {
      toast.error("Could not attach that image");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-app">Library</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Images you have uploaded, reusable across every project. Click one to attach it.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files);
          }}
          className={`border border-dashed rounded-lg p-4 text-center transition-colors ${
            dragging ? "border-kinetic bg-kinetic/5" : "border-border"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            multiple
            hidden
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
          />
          <p className="text-xs text-muted-foreground mb-2">Drag images here, or</p>
          <Button
            data-testid="library-upload"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="border-border h-8"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Choose files</>}
          </Button>
        </div>

        <div className="max-h-[46vh] overflow-auto -mx-1 px-1">
          {assets === null ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-600" />
            </div>
          ) : assets.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10">
              Nothing here yet. Anything you upload stays available to every project.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {assets.map((asset) => (
                <button
                  key={asset.id}
                  data-testid={`library-item-${asset.id}`}
                  onClick={() => pick(asset)}
                  title={`${asset.name} · ${Math.max(1, Math.round(asset.size / 1024))} kB`}
                  className="group relative aspect-square rounded-md overflow-hidden border border-border
                             bg-background hover:border-zinc-500 focus-visible:outline
                             focus-visible:outline-2 focus-visible:outline-kinetic transition-colors"
                >
                  <Thumbnail asset={asset} />
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Delete ${asset.name}`}
                    onClick={(e) => remove(asset, e)}
                    onKeyDown={(e) => { if (e.key === "Enter") remove(asset, e); }}
                    className="absolute top-1 right-1 h-6 w-6 rounded bg-zinc-900/90 border border-border
                               flex items-center justify-center text-zinc-400 opacity-0 group-hover:opacity-100
                               focus:opacity-100 hover:text-kinetic transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
